import { useEffect, ReactNode } from 'react';
import { useInView } from 'react-intersection-observer';
import { useTrackingContext, TrackingProvider } from '@zapier/toolbox-tracking';
import { EventName, SectionNameType } from './events';
import { EventAction, InteractionName, useAvroTracking } from './avro';

export type TagType =
  | 'div'
  | 'section'
  | 'article'
  | 'main'
  | 'aside'
  | 'header'
  | 'footer';

type Props = {
  children: ReactNode;
  sectionName: SectionNameType;
  as?: TagType;
  className?: string;
};

export function TrackingWrapper({
  children,
  sectionName,
  as: WrapperElement = 'div',
  className,
}: Props) {
  const { trackEvent } = useTrackingContext();
  const { emitUserInteractionEvent } = useAvroTracking();
  const [ref, inView] = useInView();

  useEffect(() => {
    if (inView) {
      emitUserInteractionEvent({
        event_action: EventAction.in_view,
        interaction_name: InteractionName.component_scrolled_into_view,
        option_selected: WrapperElement,
        custom_tracking_fields: { section_name: sectionName },
      });

      trackEvent(EventName.SCROLLED_INTO_VIEW, {
        component: sectionName,
      });
    }
  }, [
    inView,
    trackEvent,
    sectionName,
    WrapperElement,
    emitUserInteractionEvent,
  ]);

  return (
    <TrackingProvider metadata={{ event_location: sectionName }}>
      <WrapperElement className={className} ref={ref}>
        {children}
      </WrapperElement>
    </TrackingProvider>
  );
}
